import { useState } from "react";
import QueryString from "qs";
import endpointMap from "../utilities/api-endpoints-map";
import api from "../new/axios";
import { authApi, toApiError } from "../new/http-clients";
import { seededDefinitionQueryOptions } from "../new/definition-queries";
import { queryClient } from "../new/queryClient";

const methods = {
    get: "get",
    post: "post",
    put: "put",
    delete: "delete",
    patch: "patch"
};

const executeRequest = async (client, config) => {
    const definitionOptions = client === api && config.method === methods.get && config.responseType === "json"
        ? seededDefinitionQueryOptions(config.url, config.params)
        : null;

    if (definitionOptions) {
        const data = await queryClient.fetchQuery(definitionOptions);
        return { data, status: 200, config };
    }

    return client(config);
};

const useApi = () => {
    const [error, setError] = useState(null);
    const [processing, setProcessing] = useState(false);
    const [status, setStatus] = useState(-1);

    const processRequest = async (client, { endpointKey, data = null, params = null, method, headers = null, blob = false, arrayParams = false }) => {
        setError(null);
        setProcessing(true);

        try {
            const response = await executeRequest(client, {
                method,
                url: endpointMap.get(endpointKey),
                data,
                params,
                responseType: blob ? "blob" : "json",
                headers,
                paramsSerializer: arrayParams
                    ? (currentParams) => QueryString.stringify(currentParams)
                    : undefined
            });

            setStatus(response.status);
            return blob ? response : [response.data, response.status];
        } catch (requestError) {
            const normalizedError = toApiError(requestError);
            setError({
                status: normalizedError.status,
                error: normalizedError.message
            });
            setStatus(normalizedError.status || 999);
            return [null, normalizedError.status || 999];
        } finally {
            setProcessing(false);
        }
    };

    const processMultipleRequests = async (requests, method, headers = null, blob = false) => {
        setError(null);
        setProcessing(true);

        const lookupTable = new Map();
        const dataStructure = [];

        requests.forEach((request) => {
            if (!endpointMap.has(request.endpoint)) {
                throw Error(`Endpoint Key ${request.endpoint} was not found.`);
            }

            lookupTable.set(endpointMap.get(request.endpoint), request.endpoint);
        });

        try {
            const responses = await Promise.all(
                requests.map((request) =>
                    executeRequest(api, {
                        method,
                        url: endpointMap.get(request.endpoint),
                        data: request.data,
                        params: request.params,
                        responseType: blob ? "blob" : "json",
                        headers
                    })
                )
            );

            responses.forEach((response) => {
                const key = response.config.url;

                dataStructure[lookupTable.get(key)] = {
                    data: response.data,
                    status: response.status
                };
            });
        } catch (requestError) {
            const normalizedError = toApiError(requestError);
            setError({
                status: normalizedError.status,
                error: normalizedError.message
            });
        } finally {
            setProcessing(false);
        }

        return dataStructure;
    };

    return {
        error,
        processing,
        status,
        postRequest: async (endpointKey, data) => processRequest(api, { endpointKey, data, method: methods.post }),
        postFileRequest: async (endpointKey, data) => processRequest(api, {
            endpointKey,
            data,
            method: methods.post,
            headers: { "Content-Type": "multipart/form-data" }
        }),
        getRequest: async (endpointKey, params) => processRequest(api, { endpointKey, params, method: methods.get }),
        getRequestMulti: async (requests) => processMultipleRequests(requests, methods.get),
        getFileRequest: async (endpointKey, params) => processRequest(api, { endpointKey, params, method: methods.get, blob: true }),
        getRequestNoAuth: async (endpointKey, params) => processRequest(authApi, { endpointKey, params, method: methods.get }),
        putRequest: async (endpointKey, data) => processRequest(api, { endpointKey, data, method: methods.put }),
        putFileRequest: async (endpointKey, data) => processRequest(api, {
            endpointKey,
            data,
            method: methods.put,
            headers: { "Content-Type": "multipart/form-data" }
        }),
        deleteRequest: async (endpointKey, data) => processRequest(api, { endpointKey, data, method: methods.delete }),
        getRequestArray: async (endpointKey, params) => processRequest(api, {
            endpointKey,
            params,
            method: methods.get,
            arrayParams: true
        }),
        patchRequest: async (endpointKey, data) => processRequest(api, { endpointKey, data, method: methods.patch })
    };
};

export default useApi;
